import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { ListItem } from "../ui/ListItem";
import { SearchInput } from "../ui/SearchInput";
import { StepSearchInput } from "../../pages/onboarding/components/StepSearchInput";
import { displayToast } from "../app/AppToast";
import { reloadCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import {
  apiClient,
  revalidateApiQuery,
  useApiQuery,
  type Api,
} from "../../services/api";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

// Typing pause before the search request fires. The server search resolves the
// query against MeSH (PG alias cache on a warm term, one small LLM call on a cold
// one), so it is deliberately not a per-keystroke call.
const SEARCH_DEBOUNCE_MS = 450;

// Minimum query length before the server search fires. Below this we treat the
// input like an empty query (idle catalogue) rather than resolving truncated
// word fragments ("immun", "hemato") against the MeSH LLM resolver.
const MIN_SEARCH_LENGTH = 3;

// Minimal topic shape shared by the catalog (/topics) and the current selection
// (/users/me/topics) — both expose uid + translations. Inferred from the
// selection endpoint (the smaller of the two).
type TopicRef =
  Api["get"]["/webapp/private/users/me/topics"]["output"]["body"][number];

/**
 * The JuisciTopic interest editor shared by the Discovery sheet and the
 * settings Interests view: intro copy, search, the two specialty sections and
 * the batch Save button. The host renders its own title/back chrome above and
 * mounts this as the flex body; the selection seeds fresh on every mount.
 */
function TopicsInterestsEditor({
  onSaved,
  surface,
}: {
  onSaved: () => void;
  /** `sheet` takes the mobile field, `desktop` the settings modal's one. */
  surface: "sheet" | "desktop";
}): React.JSX.Element {
  const { t } = useTranslation();
  const optionsQuery = useApiQuery("/webapp/private/topics", { query: {} });
  const selectedQuery = useApiQuery("/webapp/private/users/me/topics", {});
  const [selectedUids, setSelectedUids] = useState<Set<string> | null>(null);
  const [isSaving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  // Server-side search: a substring filter over the loaded catalog can only find
  // topics whose own name contains the query, so "Oncologie" would never surface
  // "Breast Cancer". The endpoint matches every translated name AND the topics'
  // MeSH anchor names, then expands the query through the MeSH tree — see
  // `/webapp/private/topics/search`. `keepPreviousData` holds the last result
  // list on screen while the next one loads, so the list doesn't blank per keystroke.
  const searchQuery = useApiQuery(
    "/webapp/private/topics/search",
    query.length < MIN_SEARCH_LENGTH
      ? false
      : { query: { q: query }, keepPreviousData: true }
  );

  const failedQuery =
    optionsQuery.state === "error"
      ? optionsQuery
      : selectedQuery.state === "error"
        ? selectedQuery
        : null;
  if (failedQuery !== null) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-4 py-6">
        <p className="text-ui-body text-ds-slate-600">{failedQuery.message}</p>
      </div>
    );
  }
  if (optionsQuery.state !== "success" || selectedQuery.state !== "success") {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <TopicsSkeleton />
      </div>
    );
  }

  // Seed the selection from the persisted topics on first successful load. A null
  // `selectedUids` therefore means "the user hasn't toggled anything yet" — the
  // Save button stays inert and unlabelled until there is an actual edit to save.
  const isPristine = selectedUids === null;
  const selected = isPristine
    ? new Set(selectedQuery.body.map((topic) => topic.uid))
    : selectedUids;

  // Selectable list: specialty catalog ∪ already-selected topics (so a topic no
  // longer offered by the specialty still shows and stays removable), deduped by
  // uid and sorted by display name.
  const byUid = new Map<string, TopicRef>();
  for (const topic of [...optionsQuery.body, ...selectedQuery.body]) {
    if (!byUid.has(topic.uid)) byUid.set(topic.uid, topic);
  }
  const allTopics = [...byUid.values()].sort((a, b) =>
    getItemTranslation(a).localeCompare(getItemTranslation(b))
  );

  // Section split: selected topics are pulled out into a pinned first section so
  // the current selection is visible at a glance; the rest splits on the
  // server-side specialty flag — topics only present in the selection (no longer
  // offered by the specialty) fall in the "other" section.
  const inSpecialtyUids = new Set(
    optionsQuery.body
      .filter((topic) => topic.inSpecialty)
      .map((topic) => topic.uid)
  );
  const selectedTopics = allTopics.filter((topic) => selected.has(topic.uid));
  const unselectedTopics = allTopics.filter(
    (topic) => !selected.has(topic.uid)
  );
  const specialtyTopics = unselectedTopics.filter((topic) =>
    inSpecialtyUids.has(topic.uid)
  );
  const otherTopics = unselectedTopics.filter(
    (topic) => !inSpecialtyUids.has(topic.uid)
  );

  // Search results split on the server's `matchReason`: a DIRECT hit means the
  // query is in the topic's own vocabulary (a translated name or a MeSH synonym),
  // while `specialty`/`mesh` are EXPANSIONS — the query named a specialty or a
  // MeSH umbrella and the topic sits in its subtree. They get their own section
  // so "Breast Cancer" under a search for "Oncologie" reads as intentional
  // rather than as a broken filter.
  const searchResults =
    searchQuery.state === "success" ? searchQuery.body : null;
  const selectedResults =
    searchResults === null
      ? []
      : searchResults.filter((topic) => selected.has(topic.uid));
  const unselectedResults =
    searchResults === null
      ? []
      : searchResults.filter((topic) => !selected.has(topic.uid));
  const directResults = unselectedResults.filter(
    (topic) => topic.matchReason === "name" || topic.matchReason === "anchor"
  );
  const relatedResults = unselectedResults.filter(
    (topic) => topic.matchReason === "specialty" || topic.matchReason === "mesh"
  );

  function toggle(uid: string): void {
    setSelectedUids((prev) => {
      const base = prev ?? new Set(selected);
      const next = new Set(base);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  }

  async function handleSave(): Promise<void> {
    try {
      setSaving(true);
      await apiClient.patch("/webapp/private/users/me/topics", {
        json: { topicUids: [...selected] },
      });
      // The Discovery tabs read the /users/me/topics query cache directly, so
      // refresh it (reloadCurrentUser only touches /users/me — a different key).
      void revalidateApiQuery("/webapp/private/playlists/recommended");
      await revalidateApiQuery("/webapp/private/users/me/topics", {});
      await reloadCurrentUser();
      displayToast(t("Interests updated. We'll tune your feed."), "success");
      onSaved();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <p className="m-0 shrink-0 px-5 pb-4 text-[15px]/5 font-normal tracking-tight text-ds-slate-600">
        {t(
          "Select the areas you want to follow. Topics matching your specialty are listed first."
        )}
      </p>

      {allTopics.length === 0 ? (
        <p className="flex-1 px-5 text-[15px]/5 text-ds-slate-500">
          {t(
            "No topics available yet. Add a specialty to your profile to get suggestions."
          )}
        </p>
      ) : (
        <>
          <div className="shrink-0 px-5 pb-4">
            {surface === "desktop" ? (
              <SearchInput
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                placeholder={t("Search topics")}
              />
            ) : (
              <StepSearchInput
                value={search}
                onChange={setSearch}
                placeholder={t("Search topics")}
                variant="mobile"
              />
            )}
          </div>
          {searchQuery.state === "idle" ? (
            <div className="scroll-fog-y flex flex-1 flex-col content-start gap-5 overflow-y-auto px-5 pb-4">
              {selectedTopics.length > 0 ? (
                <TopicsSection
                  title={t("Your interests")}
                  topics={selectedTopics}
                  selected={selected}
                  onToggle={toggle}
                />
              ) : null}
              {specialtyTopics.length > 0 ? (
                <TopicsSection
                  title={t("Matching your specialty")}
                  topics={specialtyTopics}
                  selected={selected}
                  onToggle={toggle}
                />
              ) : null}
              {otherTopics.length > 0 ? (
                <TopicsSection
                  title={t("Other topics")}
                  topics={otherTopics}
                  selected={selected}
                  onToggle={toggle}
                />
              ) : null}
            </div>
          ) : searchQuery.state === "pending" ? (
            <TopicsSkeleton />
          ) : searchQuery.state === "error" ? (
            <p className="flex-1 px-5 text-[15px]/5 text-ds-slate-500">
              {t("Something went wrong. Please try again.")}
            </p>
          ) : searchResults !== null && searchResults.length > 0 ? (
            <div className="scroll-fog-y flex flex-1 flex-col content-start gap-5 overflow-y-auto px-5 pb-4">
              {selectedResults.length > 0 ? (
                <TopicsSection
                  title={t("Your interests")}
                  topics={selectedResults}
                  selected={selected}
                  onToggle={toggle}
                />
              ) : null}
              {directResults.length > 0 ? (
                <TopicsSection
                  title={t("Results")}
                  topics={directResults}
                  selected={selected}
                  onToggle={toggle}
                />
              ) : null}
              {relatedResults.length > 0 ? (
                <TopicsSection
                  title={t('Related to "{{query}}"', { query })}
                  topics={relatedResults}
                  selected={selected}
                  onToggle={toggle}
                />
              ) : null}
            </div>
          ) : (
            <p className="flex-1 px-5 text-[15px]/5 text-ds-slate-500">
              {t("No topic found")}
            </p>
          )}
        </>
      )}

      <div className="shrink-0 px-5 pt-3">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={isSaving || isPristine}
          onClick={() => {
            void handleSave();
          }}
        >
          {isPristine || selected.size === 0
            ? t("Save")
            : t("Save {{count}} topics", { count: selected.size })}
        </Button>
      </div>
    </div>
  );
}

// Placeholder chip widths per section, mirroring the wrapped ListItem rows so the
// loading state occupies the same band as the real list instead of a centered
// spinner overlaying the Save button.
const SKELETON_SECTIONS = [
  ["w-32", "w-24", "w-40", "w-28"],
  ["w-28", "w-36", "w-24", "w-32", "w-20"],
] as const;

function TopicsSkeleton(): React.JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col content-start gap-5 overflow-hidden px-5 pb-4"
      aria-hidden
    >
      {SKELETON_SECTIONS.map((widths, sectionIndex) => (
        <section key={sectionIndex} className="flex flex-col gap-3">
          <div className="h-4 w-32 animate-pulse rounded bg-ds-slate-100" />
          <div className="flex flex-wrap gap-2">
            {widths.map((width, chipIndex) => (
              <div
                key={chipIndex}
                className={`h-10 ${width} animate-pulse rounded-xl bg-ds-slate-100`}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function TopicsSection({
  title,
  topics,
  selected,
  onToggle,
}: {
  title: string;
  topics: TopicRef[];
  selected: ReadonlySet<string>;
  onToggle: (uid: string) => void;
}): React.JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 text-sm font-semibold tracking-tight text-ds-slate-600">
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {topics.map((topic) => (
          <ListItem
            key={topic.uid}
            affordance={{ kind: "toggle" }}
            disabled={false}
            selected={selected.has(topic.uid)}
            onClick={() => {
              onToggle(topic.uid);
            }}
            className="w-auto max-w-full text-xs"
          >
            <span className="truncate">{getItemTranslation(topic)}</span>
          </ListItem>
        ))}
      </div>
    </section>
  );
}

export { TopicsInterestsEditor };
